import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Group,
  getGroupsByOrg,
  createGroup,
  updateGroup,
  deleteGroup,
  deleteAllGroups,
  getGroupMembers,
  addMember,
  removeMember,
  setMemberBlocked,
  getRadiosByOrg,
  Radio,
} from '../../services/admin';

const STANDARD_CHANNEL_COUNT = 20;

type SortField = 'name' | 'priority' | 'member_count';
type Priority = 'low' | 'standard' | 'high';

const PRIORITY_OPTIONS: { value: Priority; label: string; weight: number }[] = [
  { value: 'low', label: 'Low', weight: -1 },
  { value: 'standard', label: 'Standard', weight: 0 },
  { value: 'high', label: 'High', weight: 1 },
];

// "echo" is a group_type value (self-monitor channel), not a separate boolean toggle anymore -
// see documentation/decisions.md. "channel" and "dispatch" were retired as selectable types (existing
// groups using either were migrated to "talkgroup", see migration 0017). "broadcast" (added
// 2026-09-09) - transmitting on it is heard by every radio in the network regardless of which
// group they currently have selected, see documentation/protocol.md "Broadcast group type".
const GROUP_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'talkgroup', label: 'Talk Group' },
  { value: 'echo', label: 'Echo' },
  { value: 'broadcast', label: 'Broadcast' },
];

const formatGroupType = (groupType: string) =>
  GROUP_TYPE_OPTIONS.find((o) => o.value === groupType)?.label || groupType || 'Talk Group';

const emptyForm: Partial<Group> = {
  name: '',
  description: '',
  group_type: 'talkgroup',
  priority: 'standard',
  echo: false,
};

export default function GroupsManager({ orgId, orgName }: { orgId: string; orgName?: string }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [radios, setRadios] = useState<Radio[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [formData, setFormData] = useState<Partial<Group>>(emptyForm);

  // "Edit channel" modal state (name/type/priority + full member management table)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editGroupType, setEditGroupType] = useState('talkgroup');
  const [editPriority, setEditPriority] = useState<Priority>('standard');
  const [editMemberIds, setEditMemberIds] = useState<Set<string>>(new Set());
  const [editBlockedIds, setEditBlockedIds] = useState<Set<string>>(new Set());
  const [initialMemberIds, setInitialMemberIds] = useState<Set<string>>(new Set());
  const [initialBlockedIds, setInitialBlockedIds] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Bulk selection + actions across multiple groups at once.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPriority, setBulkPriority] = useState<Priority>('standard');
  const [bulkGroupType, setBulkGroupType] = useState('talkgroup');

  // Bulk "add multiple radios to the selected groups at once" modal.
  const [showBulkAddRadios, setShowBulkAddRadios] = useState(false);
  const [bulkRadioIds, setBulkRadioIds] = useState<Set<string>>(new Set());
  const [bulkRadioSearch, setBulkRadioSearch] = useState('');
  const [bulkRadioModelFilter, setBulkRadioModelFilter] = useState('');

  // Excel column-mapping import: parsed workbook + header row is held here while the admin
  // matches spreadsheet columns to target fields, before anything is actually imported.
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<Record<string, unknown>[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<'name' | 'description' | 'group_type' | 'priority' | 'echo', string>>({
    name: '',
    description: '',
    group_type: '',
    priority: '',
    echo: '',
  });
  const [showMappingModal, setShowMappingModal] = useState(false);

  // Skip auto-refresh while a form/modal is open or a bulk op is running.
  const busyRef = useRef(false);
  useEffect(() => {
    busyRef.current = showForm || !!editingGroup || busy || showBulkAddRadios;
  }, [showForm, editingGroup, busy, showBulkAddRadios]);

  useEffect(() => {
    loadGroups();
    loadRadios();
    const interval = setInterval(() => {
      if (!busyRef.current) loadGroups(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [orgId]);

  const loadGroups = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getGroupsByOrg(orgId);
      setGroups(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadRadios = async () => {
    try {
      const data = await getRadiosByOrg(orgId);
      setRadios(data);
    } catch (err) {
      console.error('Failed to load radios:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createGroup(orgId, {
        name: formData.name || '',
        description: formData.description || '',
        group_type: formData.group_type || 'talkgroup',
        priority: formData.priority || 'standard',
        echo: formData.echo || false,
      });
      setShowForm(false);
      setFormData(emptyForm);
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save group');
    }
  };

  const openEdit = async (group: Group) => {
    setEditingGroup(group);
    setEditName(group.name);
    setEditDescription(group.description || '');
    setEditGroupType(group.group_type || 'talkgroup');
    setEditPriority((group.priority as Priority) || 'standard');
    setMemberSearch('');
    setModelFilter('');
    setError(null);
    try {
      const data = await getGroupMembers(orgId, group.id);
      const memberIds = new Set(data.map((m) => m.radio_id));
      const blockedIds = new Set(data.filter((m) => m.ptt_blocked).map((m) => m.radio_id));
      setEditMemberIds(memberIds);
      setEditBlockedIds(blockedIds);
      setInitialMemberIds(memberIds);
      setInitialBlockedIds(blockedIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    }
  };

  const closeEdit = () => {
    setEditingGroup(null);
    setEditMemberIds(new Set());
    setEditBlockedIds(new Set());
    setInitialMemberIds(new Set());
    setInitialBlockedIds(new Set());
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this group? This will remove all member assignments.')) return;
    setError(null);
    try {
      await deleteGroup(orgId, id);
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group');
    }
  };

  const handleDeleteAll = async () => {
    if (groups.length === 0) return;
    if (!confirm(`Delete all ${groups.length} group(s) in this network? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAllGroups(orgId);
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete all groups');
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkSetPriority = async () => {
    setBusy(true);
    setError(null);
    try {
      await Promise.all(
        [...selectedIds].map((id) => {
          const group = groups.find((g) => g.id === id);
          if (!group) return Promise.resolve();
          return updateGroup(orgId, id, {
            name: group.name,
            description: group.description,
            group_type: group.group_type,
            priority: bulkPriority,
            echo: group.echo,
          });
        })
      );
      clearSelection();
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update priority for selected groups');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkSetType = async () => {
    setBusy(true);
    setError(null);
    try {
      await Promise.all(
        [...selectedIds].map((id) => {
          const group = groups.find((g) => g.id === id);
          if (!group) return Promise.resolve();
          return updateGroup(orgId, id, {
            name: group.name,
            description: group.description,
            group_type: bulkGroupType,
            priority: group.priority,
            echo: group.echo,
          });
        })
      );
      clearSelection();
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update type for selected groups');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected group(s)? This will remove all member assignments.`)) return;
    setBusy(true);
    setError(null);
    try {
      await Promise.all([...selectedIds].map((id) => deleteGroup(orgId, id)));
      clearSelection();
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected groups');
    } finally {
      setBusy(false);
    }
  };

  const openBulkAddRadios = () => {
    setBulkRadioIds(new Set());
    setBulkRadioSearch('');
    setBulkRadioModelFilter('');
    setError(null);
    setShowBulkAddRadios(true);
  };

  const closeBulkAddRadios = () => setShowBulkAddRadios(false);

  const toggleBulkRadio = (radioId: string) => {
    setBulkRadioIds((prev) => {
      const next = new Set(prev);
      if (next.has(radioId)) next.delete(radioId);
      else next.add(radioId);
      return next;
    });
  };

  // Adds every selected radio as a member of every selected group (N groups x M radios).
  // Duplicates (a radio already in a group) are tolerated per-pair, matching the same
  // best-effort pattern already used by handleAddStandardChannels/importGroupRecords.
  const handleBulkAddRadios = async () => {
    if (selectedIds.size === 0 || bulkRadioIds.size === 0) return;
    setBusy(true);
    setError(null);
    let added = 0;
    let skipped = 0;
    for (const groupId of selectedIds) {
      for (const radioId of bulkRadioIds) {
        try {
          await addMember(orgId, groupId, radioId);
          added += 1;
        } catch {
          skipped += 1;
        }
      }
    }
    closeBulkAddRadios();
    clearSelection();
    await loadGroups();
    setBusy(false);
    if (skipped > 0) {
      setError(`Added ${added} membership(s); skipped ${skipped} (already a member).`);
    }
  };

  const handleAddStandardChannels = async () => {
    if (!confirm(`Create ${STANDARD_CHANNEL_COUNT} standard channels (Kanal 01-${STANDARD_CHANNEL_COUNT}) plus a "Selvtest" echo group?`)) return;
    setBusy(true);
    setError(null);
    let created = 0;
    let skipped = 0;
    for (let i = 1; i <= STANDARD_CHANNEL_COUNT; i++) {
      const name = `Kanal ${String(i).padStart(2, '0')}`;
      try {
        await createGroup(orgId, {
          name,
          description: '',
          group_type: 'talkgroup',
          priority: 'standard',
          echo: false,
        });
        created += 1;
      } catch {
        // Most likely a duplicate name (unique per network) — skip and keep going.
        skipped += 1;
      }
    }
    // Group #21: a dedicated echo (self-monitor) group so radios can test their own audio path.
    try {
      await createGroup(orgId, {
        name: 'Selvtest',
        description: '',
        group_type: 'echo',
        priority: 'standard',
        echo: false,
      });
      created += 1;
    } catch {
      skipped += 1;
    }
    await loadGroups();
    setBusy(false);
    if (skipped > 0) {
      setError(`Created ${created} channel(s); skipped ${skipped} that already existed.`);
    }
  };

  // Builds a filesystem-safe export filename embedding the network name and a timestamp, e.g.
  // "groups-Rock-i-Frederikshavn-2026-09-09-183045.xlsx".
  const buildExportFilename = (extension: string) => {
    const safeName = (orgName || 'network').trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'network';
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `groups-${safeName}-${timestamp}.${extension}`;
  };

  const handleExportXlsx = () => {
    const rows = groups.map((g) => ({
      Name: g.name,
      Description: g.description,
      Type: g.group_type,
      Priority: g.priority,
      Echo: g.echo ? 'TRUE' : 'FALSE',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Groups');
    XLSX.writeFile(workbook, buildExportFilename('xlsx'));
  };

  const handleExportJson = () => {
    const rows = groups.map((g) => ({
      name: g.name,
      description: g.description,
      group_type: g.group_type,
      priority: g.priority,
      echo: g.echo,
    }));
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildExportFilename('json');
    link.click();
    URL.revokeObjectURL(url);
  };

  const normalizePriority = (value: unknown): Priority => {
    const raw = String(value ?? 'standard').trim().toLowerCase();
    return (['low', 'standard', 'high'].includes(raw) ? raw : 'standard') as Priority;
  };

  const truthy = (value: unknown) =>
    value === true || ['TRUE', 'YES', '1'].includes(String(value ?? '').trim().toUpperCase());

  // Applies parsed rows (from either XLSX or JSON) via the create API, tallying created/skipped.
  const importGroupRecords = async (records: Partial<Group>[]) => {
    let created = 0;
    let skipped = 0;
    for (const record of records) {
      const name = String(record.name ?? '').trim();
      if (!name) {
        skipped += 1;
        continue;
      }
      try {
        await createGroup(orgId, {
          name,
          description: String(record.description ?? ''),
          group_type: String(record.group_type ?? 'talkgroup'),
          priority: normalizePriority(record.priority),
          echo: truthy(record.echo),
        });
        created += 1;
      } catch {
        skipped += 1;
      }
    }
    await loadGroups();
    if (skipped > 0) {
      setError(`Imported ${created} group(s); skipped ${skipped} (missing name or duplicate).`);
    }
  };

  // Best-effort guess so the common case (headers already named Name/Description/Type/
  // Priority/Echo) needs no manual work, while any other header layout can still be mapped.
  const guessMapping = (headers: string[]): typeof fieldMapping => {
    const find = (...candidates: string[]) =>
      headers.find((h) => candidates.includes(h.trim().toLowerCase())) || '';
    return {
      name: find('name'),
      description: find('description', 'desc'),
      group_type: find('type', 'group_type', 'group type'),
      priority: find('priority'),
      echo: find('echo'),
    };
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const isJson = file.name.toLowerCase().endsWith('.json');
    if (isJson) {
      // JSON import already uses the app's own field names directly - no mapping needed.
      setBusy(true);
      setError(null);
      try {
        const records = JSON.parse(await file.text()) as Partial<Group>[];
        if (!Array.isArray(records)) throw new Error('not an array');
        await importGroupRecords(records);
      } catch {
        setError('Failed to import file — expected a JSON (.json) file containing an array with a name field.');
      } finally {
        setBusy(false);
      }
      return;
    }

    // Excel import: parse headers/rows and let the admin match spreadsheet columns to fields
    // before importing anything (spreadsheet column names often won't match ours exactly).
    setError(null);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
      if (rows.length === 0) throw new Error('empty sheet');
      const headers = Object.keys(rows[0]);
      setImportHeaders(headers);
      setImportRows(rows);
      setFieldMapping(guessMapping(headers));
      setShowMappingModal(true);
    } catch {
      setError('Failed to read Excel file — expected a .xlsx file with a header row.');
    }
  };

  const closeMappingModal = () => {
    setShowMappingModal(false);
    setImportHeaders([]);
    setImportRows([]);
  };

  const applyMappingAndImport = async () => {
    if (!fieldMapping.name) {
      setError('Select which column contains the group name before importing.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const records: Partial<Group>[] = importRows.map((row) => ({
        name: String(row[fieldMapping.name] ?? ''),
        description: fieldMapping.description ? String(row[fieldMapping.description] ?? '') : '',
        group_type: fieldMapping.group_type ? String(row[fieldMapping.group_type] ?? 'talkgroup') : 'talkgroup',
        priority: normalizePriority(fieldMapping.priority ? row[fieldMapping.priority] : undefined),
        echo: fieldMapping.echo ? truthy(row[fieldMapping.echo]) : false,
      }));
      await importGroupRecords(records);
      closeMappingModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import mapped rows');
    } finally {
      setBusy(false);
    }
  };

  const modelOptions = Array.from(new Set(radios.map((r) => r.model).filter((m): m is string => !!m)));


  const filteredMemberRadios = radios.filter((r) => {
    const matchesSearch =
      (r.name || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
      (r.radio_id || '').toLowerCase().includes(memberSearch.toLowerCase());
    const matchesModel = !modelFilter || r.model === modelFilter;
    return matchesSearch && matchesModel;
  });

  const allFilteredAreMembers = filteredMemberRadios.length > 0 && filteredMemberRadios.every((r) => editMemberIds.has(r.id));
  const filteredMembers = filteredMemberRadios.filter((r) => editMemberIds.has(r.id));
  const allFilteredMembersBlocked = filteredMembers.length > 0 && filteredMembers.every((r) => editBlockedIds.has(r.id));

  const toggleMember = (radioId: string) => {
    setEditMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(radioId)) {
        next.delete(radioId);
        setEditBlockedIds((blocked) => {
          const nb = new Set(blocked);
          nb.delete(radioId);
          return nb;
        });
      } else {
        next.add(radioId);
      }
      return next;
    });
  };

  const toggleBlocked = (radioId: string) => {
    setEditBlockedIds((prev) => {
      const next = new Set(prev);
      if (next.has(radioId)) next.delete(radioId);
      else next.add(radioId);
      return next;
    });
  };

  const toggleSelectAllUsers = () => {
    setEditMemberIds((prev) => {
      const next = new Set(prev);
      if (allFilteredAreMembers) {
        filteredMemberRadios.forEach((r) => next.delete(r.id));
        setEditBlockedIds((blocked) => {
          const nb = new Set(blocked);
          filteredMemberRadios.forEach((r) => nb.delete(r.id));
          return nb;
        });
      } else {
        filteredMemberRadios.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const toggleBlockAllUsers = () => {
    setEditBlockedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredMembersBlocked) {
        filteredMembers.forEach((r) => next.delete(r.id));
      } else {
        filteredMembers.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const handleSaveEdit = async () => {
    if (!editingGroup) return;
    setEditSaving(true);
    setError(null);
    try {
      await updateGroup(orgId, editingGroup.id, {
        name: editName,
        description: editDescription,
        group_type: editGroupType,
        priority: editPriority,
        echo: editingGroup.echo,
      });

      const toAdd = [...editMemberIds].filter((id) => !initialMemberIds.has(id));
      const toRemove = [...initialMemberIds].filter((id) => !editMemberIds.has(id));
      await Promise.all(toAdd.map((id) => addMember(orgId, editingGroup.id, id)));
      await Promise.all(toRemove.map((id) => removeMember(orgId, editingGroup.id, id)));

      const blockChanges = [...editMemberIds].filter((id) => {
        const previousBlocked = initialMemberIds.has(id) ? initialBlockedIds.has(id) : false;
        return previousBlocked !== editBlockedIds.has(id);
      });
      await Promise.all(blockChanges.map((id) => setMemberBlocked(orgId, editingGroup.id, id, editBlockedIds.has(id))));

      closeEdit();
      await loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save channel');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortIndicator = (field: SortField) => (sortField === field ? (sortAsc ? ' ▲' : ' ▼') : '');

  const priorityOrder: Record<string, number> = { low: 0, standard: 1, high: 2 };

  const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  const sortedGroups = [...filteredGroups].sort((a, b) => {
    // Echo (self-monitor) groups always sort to the bottom, regardless of the active sort
    // field/direction - "echo" is a group_type value, not a separate boolean anymore.
    const aEcho = a.group_type === 'echo';
    const bEcho = b.group_type === 'echo';
    if (aEcho !== bEcho) return aEcho ? 1 : -1;

    let va: string | number;
    let vb: string | number;
    if (sortField === 'priority') {
      va = priorityOrder[a.priority] ?? 1;
      vb = priorityOrder[b.priority] ?? 1;
    } else if (sortField === 'member_count') {
      va = a.member_count ?? 0;
      vb = b.member_count ?? 0;
    } else {
      va = a.name.toLowerCase();
      vb = b.name.toLowerCase();
    }
    if (va < vb) return sortAsc ? -1 : 1;
    if (va > vb) return sortAsc ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">📢 Groups</h2>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search groups..."
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
      />

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            setShowForm(true);
            setError(null);
            setFormData(emptyForm);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          + Add Group
        </button>
        <button
          onClick={handleExportXlsx}
          disabled={groups.length === 0}
          className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
        >
          ⬇ Export (.xlsx)
        </button>
        <button
          onClick={handleExportJson}
          disabled={groups.length === 0}
          className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
        >
          ⬇ Export (.json)
        </button>
        <label className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded cursor-pointer">
          ⬆ Import (.xlsx/.json)
          <input
            type="file"
            accept=".xlsx,.json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json"
            onChange={handleImportFile}
            className="hidden"
            disabled={busy}
          />
        </label>
        <button
          onClick={handleDeleteAll}
          disabled={busy || groups.length === 0}
          className="bg-transparent hover:bg-red-900/30 disabled:opacity-50 text-red-400 border border-red-700 font-bold py-2 px-4 rounded"
        >
          🗑 Delete All Groups
        </button>
        <button
          onClick={handleAddStandardChannels}
          disabled={busy}
          className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded ml-auto"
        >
          📻 Add Standard {STANDARD_CHANNEL_COUNT} groups
        </button>
      </div>

      {error && (
        <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-200">
          {error}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold mb-4">Create New Group</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Group Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                placeholder="e.g., Operations"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                placeholder="Optional description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                <select
                  value={formData.group_type || 'talkgroup'}
                  onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  {GROUP_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
                <select
                  value={formData.priority || 'standard'}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as Group['priority'] })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="low">Low</option>
                  <option value="standard">Standard</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Save Group
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData(emptyForm);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-gray-800 border border-blue-700 rounded-lg p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-300 whitespace-nowrap">
            {selectedIds.size} group{selectedIds.size === 1 ? '' : 's'} selected
          </span>

          <div className="flex items-center gap-2">
            <select
              value={bulkPriority}
              onChange={(e) => setBulkPriority(e.target.value as Priority)}
              disabled={busy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkSetPriority}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Set Priority
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkGroupType}
              onChange={(e) => setBulkGroupType(e.target.value)}
              disabled={busy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {GROUP_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkSetType}
              disabled={busy}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Set Type
            </button>
          </div>

          <button
            onClick={openBulkAddRadios}
            disabled={busy}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
          >
            + Add Radios...
          </button>

          <button
            onClick={handleBulkDelete}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
          >
            Delete Selected
          </button>

          <button
            onClick={clearSelection}
            disabled={busy}
            className="ml-auto bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 text-sm font-medium py-1.5 px-3 rounded"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Groups Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading groups...</p>
        </div>
      ) : sortedGroups.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          <p className="text-lg">{groups.length === 0 ? 'No groups found. Create one to get started.' : 'No groups match your search.'}</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-900 text-gray-400 text-sm">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={sortedGroups.length > 0 && sortedGroups.every((g) => selectedIds.has(g.id))}
                    onChange={() => toggleSelectAllVisible(sortedGroups.map((g) => g.id))}
                  />
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('name')}>
                  Name{sortIndicator('name')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('priority')}>
                  Priority{sortIndicator('priority')}
                </th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('member_count')}>
                  Members{sortIndicator('member_count')}
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((group) => (
                <tr key={group.id} className="border-t border-gray-700">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(group.id)}
                      onChange={() => toggleSelected(group.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openEdit(group)} className="font-medium text-left hover:text-blue-400">
                      {group.name}
                    </button>
                    {group.description && <p className="text-xs text-gray-400 mt-0.5">{group.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-300 capitalize">{group.priority || 'standard'}</td>
                  <td className="px-4 py-3 text-gray-300">{formatGroupType(group.group_type)}</td>
                  <td className="px-4 py-3 text-gray-300">{group.member_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(group)}
                        title="Edit"
                        className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded w-8 h-8 flex items-center justify-center"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(group.id)}
                        title="Delete"
                        className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded w-8 h-8 flex items-center justify-center"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk Add Radios Modal - adds every checked radio as a member of every currently
          selected group (N groups x M radios) in one action. */}
      {showBulkAddRadios && (
        <div data-management-modal className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Add Radios to {selectedIds.size} Group{selectedIds.size === 1 ? '' : 's'}</h3>
              <button onClick={closeBulkAddRadios} className="text-gray-400 hover:text-white text-2xl leading-none">
                ×
              </button>
            </div>
            <p className="text-sm text-gray-400">
              Select the radios to add - each one will be added as a member of every selected group.
            </p>

            {error && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">{error}</div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={bulkRadioSearch}
                onChange={(e) => setBulkRadioSearch(e.target.value)}
                placeholder="Search radios..."
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
              <select
                value={bulkRadioModelFilter}
                onChange={(e) => setBulkRadioModelFilter(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option value="">All models</option>
                {modelOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {(() => {
              const filtered = radios.filter((r) => {
                const matchesSearch =
                  (r.name || '').toLowerCase().includes(bulkRadioSearch.toLowerCase()) ||
                  (r.radio_id || '').toLowerCase().includes(bulkRadioSearch.toLowerCase());
                const matchesModel = !bulkRadioModelFilter || r.model === bulkRadioModelFilter;
                return matchesSearch && matchesModel;
              });
              const allSelected = filtered.length > 0 && filtered.every((r) => bulkRadioIds.has(r.id));
              return (
                <>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() =>
                        setBulkRadioIds(allSelected ? new Set() : new Set(filtered.map((r) => r.id)))
                      }
                    />
                    Select all ({filtered.length})
                  </label>
                  <div className="border border-gray-600 rounded max-h-64 overflow-y-auto divide-y divide-gray-700">
                    {filtered.map((r) => (
                      <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/50 cursor-pointer">
                        <input type="checkbox" checked={bulkRadioIds.has(r.id)} onChange={() => toggleBulkRadio(r.id)} />
                        <span className="font-mono text-xs text-gray-400">{r.radio_id}</span>
                        {r.name || '—'}
                      </label>
                    ))}
                    {filtered.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No radios match.</p>}
                  </div>
                </>
              );
            })()}

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={closeBulkAddRadios}
                disabled={busy}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddRadios}
                disabled={busy || bulkRadioIds.size === 0}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {busy ? 'Adding...' : `Add ${bulkRadioIds.size || ''} Radio${bulkRadioIds.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Column Mapping Modal */}
      {showMappingModal && (
        <div data-management-modal className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Match Excel Columns</h3>
              <button onClick={closeMappingModal} className="text-gray-400 hover:text-white text-2xl leading-none">
                ×
              </button>
            </div>
            <p className="text-sm text-gray-400">
              Choose which column in your spreadsheet ({importRows.length} row{importRows.length === 1 ? '' : 's'} found)
              corresponds to each field. Fields left as "-- None --" are skipped/defaulted.
            </p>

            {error && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">{error}</div>
            )}

            <div className="space-y-3">
              {(
                [
                  { key: 'name', label: 'Name', required: true },
                  { key: 'description', label: 'Description', required: false },
                  { key: 'group_type', label: 'Type', required: false },
                  { key: 'priority', label: 'Priority', required: false },
                  { key: 'echo', label: 'Echo', required: false },
                ] as const
              ).map((field) => (
                <label key={field.key} className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">
                    {field.label}{field.required ? ' *' : ''}
                  </p>
                  <select
                    value={fieldMapping[field.key]}
                    onChange={(e) => setFieldMapping({ ...fieldMapping, [field.key]: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">-- None --</option>
                    {importHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={closeMappingModal}
                disabled={busy}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={applyMappingAndImport}
                disabled={busy || !fieldMapping.name}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {busy ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Channel Modal */}
      {editingGroup && (
        <div data-management-modal className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Edit Channel</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-white text-2xl leading-none">
                ×
              </button>
            </div>

            {error && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            <label className="block">
              <p className="text-sm font-medium text-gray-300 mb-1">Name</p>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
              />
            </label>

            <label className="block">
              <p className="text-sm font-medium text-gray-300 mb-1">Description</p>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                placeholder="Optional description"
                rows={2}
              />
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <p className="text-sm font-medium text-gray-300 mb-1">Type</p>
                <select
                  value={editGroupType}
                  onChange={(e) => setEditGroupType(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  {GROUP_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <p className="text-sm font-medium text-gray-300 mb-1">Priority</p>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value as Priority)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label} ({p.weight})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="border-t border-gray-700 pt-4 space-y-3">
              <h4 className="text-sm font-bold text-gray-300 uppercase">Members</h4>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={allFilteredAreMembers} onChange={toggleSelectAllUsers} />
                  Select all users
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={allFilteredMembersBlocked} onChange={toggleBlockAllUsers} />
                  Block PTT for all
                </label>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search..."
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500"
                />
                <select
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="">All models</option>
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-900 text-gray-400 sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Member</th>
                      <th className="px-3 py-2">Block PTT</th>
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMemberRadios.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                          No radios match.
                        </td>
                      </tr>
                    ) : (
                      filteredMemberRadios.map((radio) => {
                        const isMember = editMemberIds.has(radio.id);
                        return (
                          <tr key={radio.id} className="border-t border-gray-700">
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={isMember} onChange={() => toggleMember(radio.id)} />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={editBlockedIds.has(radio.id)}
                                disabled={!isMember}
                                onChange={() => toggleBlocked(radio.id)}
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-200">
                              {radio.name || radio.radio_id} — {radio.radio_id}
                            </td>
                            <td className="px-3 py-2 text-gray-300">{radio.model || '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={closeEdit}
                disabled={editSaving}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-gray-400 text-sm">Total groups: {groups.length}</p>
    </div>
  );
}
